import React from "react";
import PropTypes from "prop-types";
import MultiStyleButton from "../../../../systemUtils/MultiStyleButton";

const UserStep3ApprovalGate = ({
  setCardStep,
  companyStructure,
  userSystem,
  onChange,
  finishFlow,
}) => {
  const isApprovalRequired = userSystem.approvalGate?.isRequired || false;
  const isMultipleApprovals =
    userSystem.approvalGate?.isMultipleApprovals || false;

  const selectedApproverGroups = userSystem.approvalGate?.approverGroups || [];
  const selectedApproverRoles = userSystem.approvalGate?.approverRoles || [];

  const handleApprovalRequiredCheckbox = (e) => {
    const newIsRequired = e.target.checked;
    if (!newIsRequired) {
      // turn off approvals -> clear everything
      onChange({
        ...userSystem,
        approvalGate: {
          ...userSystem.approvalGate,
          isRequired: false,
          isMultipleApprovals: false,
          approverGroups: [],
          approverRoles: [],
        },
      });
      return;
    }
    // turning on
    onChange({
      ...userSystem,
      approvalGate: {
        ...userSystem.approvalGate,
        isRequired: true,
        isMultipleApprovals:
          userSystem.approvalGate?.isMultipleApprovals || false,
      },
    });
  };

  const handleMultipleApprovalsCheckbox = (e) => {
    const newIsMultiple = e.target.checked;
    if (newIsMultiple) {
      // turn on multiple => clear group-based
      onChange({
        ...userSystem,
        approvalGate: {
          ...userSystem.approvalGate,
          isMultipleApprovals: true,
          approverGroups: [],
          approverRoles: userSystem.approvalGate?.approverRoles || [],
        },
      });
    } else {
      // turn off multiple => revert to group-based
      onChange({
        ...userSystem,
        approvalGate: {
          ...userSystem.approvalGate,
          isMultipleApprovals: false,
          approverRoles: [],
          approverGroups: userSystem.approvalGate?.approverGroups || [],
        },
      });
    }
  };

  const handleCheckboxForGroup = (groupName) => {
    const current = selectedApproverGroups;
    let newArr;
    if (current.includes(groupName)) {
      newArr = current.filter((g) => g !== groupName);
    } else {
      newArr = [...current, groupName];
    }
    onChange({
      ...userSystem,
      approvalGate: {
        ...userSystem.approvalGate,
        isMultipleApprovals: false,
        approverGroups: newArr,
      },
    });
  };

  const allRoles = companyStructure?.userGroups
    ? companyStructure.userGroups.flatMap((g) =>
        g.roles.map((r) => ({ groupName: g.groupName, roleName: r.roleName }))
      )
    : [];

  const getRoleKey = (groupName, roleName) => `${groupName}||${roleName}`;

  const handleCheckboxForRole = (roleKey) => {
    let newArr;
    if (selectedApproverRoles.includes(roleKey)) {
      newArr = selectedApproverRoles.filter((r) => r !== roleKey);
    } else {
      newArr = [...selectedApproverRoles, roleKey];
    }
    onChange({
      ...userSystem,
      approvalGate: {
        ...userSystem.approvalGate,
        isMultipleApprovals: true,
        approverRoles: newArr,
      },
    });
  };

  const handleContinue = () => {
    // validation
    if (!isApprovalRequired) {
      setCardStep(4); // proceed
      return;
    }
    if (!isMultipleApprovals) {
      // group-based
      if (selectedApproverGroups.length === 0) {
        alert("Please select at least one user group if approval is required.");
        return;
      }
    } else {
      // role-based
      if (selectedApproverRoles.length === 0) {
        alert(
          "Please select at least one user role if multiple approvals are required."
        );
        return;
      }
    }
    // if all good
    setCardStep(4);
  };

  return (
    <div>
      <h2 className="card-title">Add Users Approval Gate</h2>
      <p className="card-subtitle">
        By default, no approval is required to add new users. If you want an
        approval process, check the box below. You can also enable multiple
        approvals, which means everyone in the selected roles must approve
        before the new user is added.
      </p>

      {/* 1) Require Approval? */}
      <div className="role-toggle-row">
        <label>
          <input
            type="checkbox"
            checked={isApprovalRequired}
            onChange={handleApprovalRequiredCheckbox}
          />
          Require Approval before a new user is added?
        </label>
      </div>

      {isApprovalRequired && (
        <div className="approval-frame">
          <div className="multiple-approvals-container">
            <label className="multiple-approvals-check">
              <input
                type="checkbox"
                checked={isMultipleApprovals}
                onChange={handleMultipleApprovalsCheckbox}
              />
              Need multiple approvals?
            </label>
            <p className="multiple-approvals-explainer">
              Sometimes you need extra security. With multiple approvals,
              <strong> all selected roles</strong> must grant approval before
              the user is officially added.
            </p>
          </div>

          {!isMultipleApprovals ? (
            <>
              <p className="approval-title">
                Which user groups will have approval rights?
              </p>
              {companyStructure?.userGroups?.map((g) => (
                <label key={g.groupName} className="approval-group-label">
                  <input
                    type="checkbox"
                    checked={selectedApproverGroups.includes(g.groupName)}
                    onChange={() => handleCheckboxForGroup(g.groupName)}
                  />
                  {g.groupName}
                </label>
              ))}
            </>
          ) : (
            <>
              <p className="approval-title">
                Which user roles will have approval rights?
              </p>
              {allRoles.map((roleObj) => {
                const roleKey = getRoleKey(roleObj.groupName, roleObj.roleName);
                const isChecked = selectedApproverRoles.includes(roleKey);
                return (
                  <label key={roleKey} className="approval-group-label">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleCheckboxForRole(roleKey)}
                    />
                    {roleObj.groupName} - {roleObj.roleName}
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}

      <div className="continue-button-container">
        <MultiStyleButton
          label="Back"
          buttonType="cancel"
          onClick={() => setCardStep(2)}
        />
        <MultiStyleButton
          label="Continue"
          buttonType="approve"
          onClick={handleContinue}
        />
      </div>
    </div>
  );
};

UserStep3ApprovalGate.propTypes = {
  setCardStep: PropTypes.func.isRequired,
  companyStructure: PropTypes.object.isRequired,
  userSystem: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default UserStep3ApprovalGate;
